// The weather state (decision 0048: "game state on a simple schedule"): which weather holds, since when and until
// when (in the clock's total hours), the one it is fading in from, and how wet the ground is. Transitions come from
// the game's table: each weather lasts a seeded time within its range, then the next is drawn by the row's weights.
// Randomness is the game's seeded stream passed in (`rng`), so the same seed gives the same skies and a save restores
// them. Pure data and functions; the look reads the blend (`weatherBlend`), the sim steps the state (`stepWeather`).

/** One weather's row: how long it lasts, what may follow it, how fast it fades in, what it does to the ground. */
export interface WeatherRow {
  id: string;
  /** Game hours it lasts: [min, max], drawn uniformly. */
  hours: readonly [number, number];
  /** Weights of what follows (ids of rows; the row's own id is allowed: the same weather again). */
  next: Readonly<Record<string, number>>;
  /** Game hours it takes to fade in over the one before. */
  fade: number;
  /** Wetness per game hour while it holds (negative dries the ground); wetness stays in [0, 1]. */
  wetting: number;
}

/** The weather's state (saved). Hours are the clock's total hours. */
export interface WeatherState {
  current: string;
  /** The weather fading out under `current` (the same id once the fade is done or at the start). */
  previous: string;
  /** When `current` began. */
  since: number;
  /** When the next weather is drawn. */
  until: number;
  /** Ground wetness, 0 to 1. */
  wet: number;
}

/** What the look reads: each weather's weight now (summing to 1) and the wetness. */
export interface WeatherBlend {
  mix: Record<string, number>;
  wet: number;
}

/** A change of weather in a step. */
export interface WeatherChange {
  from: string;
  to: string;
  at: number;
}

/** Planted defects for the weather's checks: `unseeded` draws from Math.random instead of the stream it is given. */
export const WEATHER_FAULTS = { unseeded: false };

/** Throws on a table that can't work: no rows, a bad range or fade, a `next` naming an unknown row, no positive weight. */
export function validateWeather(table: readonly WeatherRow[]): void {
  if (!table.length) throw new Error("@rpg/world weather: an empty table");
  const ids = new Set(table.map((r) => r.id));
  if (ids.size !== table.length) throw new Error("@rpg/world weather: two rows with one id");
  for (const r of table) {
    const [a, b] = r.hours;
    if (!(a > 0 && b >= a && Number.isFinite(b))) throw new Error(`@rpg/world weather: "${r.id}" hours [${a}, ${b}] must be 0 < min ≤ max`);
    if (!(r.fade >= 0) || !Number.isFinite(r.wetting)) throw new Error(`@rpg/world weather: "${r.id}" bad fade or wetting`);
    let total = 0;
    for (const [k, w] of Object.entries(r.next)) {
      if (!ids.has(k)) throw new Error(`@rpg/world weather: "${r.id}" names an unknown next "${k}"`);
      if (!(w >= 0)) throw new Error(`@rpg/world weather: "${r.id}" weight for "${k}" must be ≥ 0`);
      total += w;
    }
    if (!(total > 0)) throw new Error(`@rpg/world weather: "${r.id}" has no next weather with a positive weight`);
  }
}

const rowOf = (table: readonly WeatherRow[], id: string): WeatherRow => {
  const r = table.find((x) => x.id === id);
  if (!r) throw new Error(`@rpg/world weather: no row "${id}"`);
  return r;
};

const draw = (rng: () => number): number => (WEATHER_FAULTS.unseeded ? Math.random() : rng());

/** How long a row lasts this time (one draw). */
function lasts(r: WeatherRow, rng: () => number): number {
  return r.hours[0] + (r.hours[1] - r.hours[0]) * draw(rng);
}

/** The next weather after `row`, by its weights (one draw). Weights are read in the row's key order. */
export function pickNext(row: WeatherRow, rng: () => number): string {
  const entries = Object.entries(row.next).filter(([, w]) => w > 0);
  const total = entries.reduce((a, [, w]) => a + w, 0);
  let x = draw(rng) * total;
  for (const [k, w] of entries) {
    if (x < w) return k;
    x -= w;
  }
  return entries[entries.length - 1]![0];
}

/** A fresh weather state: `start` holding from `now` for a drawn time, dry ground unless given. */
export function createWeatherState(table: readonly WeatherRow[], start: string, now: number, rng: () => number, wet = 0): WeatherState {
  const r = rowOf(table, start);
  return { current: start, previous: start, since: now, until: now + lasts(r, rng), wet: clamp01(wet) };
}

/** Force a weather now (a debug switch, a script): it fades in over its own fade time and lasts a drawn time. */
export function setWeather(s: WeatherState, table: readonly WeatherRow[], id: string, now: number, rng: () => number, o: { instant?: boolean } = {}): WeatherChange | null {
  const r = rowOf(table, id);
  const from = s.current;
  s.previous = o.instant ? id : s.current;
  s.current = id;
  s.since = o.instant ? now - r.fade : now;
  s.until = now + lasts(r, rng);
  return from === id ? null : { from, to: id, at: now };
}

/** Step the weather to total hour `now`, `dtHours` after the last step (mutates `s`): wetness moves by the blended
 *  rate, and once `until` passes the next weather is drawn and begins fading in. Returns the changes, in order. */
export function stepWeather(s: WeatherState, table: readonly WeatherRow[], now: number, dtHours: number, rng: () => number): WeatherChange[] {
  const out: WeatherChange[] = [];
  // a long jump (a skipped night) may pass several draws: each takes over at its predecessor's end
  for (let guard = 0; now >= s.until && guard < 64; guard++) {
    const at = s.until;
    const next = pickNext(rowOf(table, s.current), rng);
    const r = rowOf(table, next);
    if (next !== s.current) out.push({ from: s.current, to: next, at });
    s.previous = s.current;
    s.current = next;
    s.since = at;
    s.until = at + lasts(r, rng);
  }
  const b = weatherBlend(s, table, now);
  let rate = 0;
  for (const [k, w] of Object.entries(b.mix)) rate += w * rowOf(table, k).wetting;
  s.wet = clamp01(s.wet + rate * Math.max(0, dtHours));
  return out;
}

/** The blend at total hour `now`: the current weather's weight rises from 0 to 1 over its fade (smoothstep), the
 *  previous one's falls with it. */
export function weatherBlend(s: Readonly<WeatherState>, table: readonly WeatherRow[], now: number): WeatherBlend {
  const r = rowOf(table, s.current);
  const t = r.fade > 0 ? clamp01((now - s.since) / r.fade) : 1;
  const k = t * t * (3 - 2 * t);
  const mix: Record<string, number> = {};
  if (s.previous === s.current || k >= 1) mix[s.current] = 1;
  else {
    mix[s.previous] = 1 - k;
    mix[s.current] = k;
  }
  return { mix, wet: s.wet };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
